import { useMemo } from 'react'
import { useLocation, useParams } from 'react-router'
import type { NavContext } from '@/components/nav-v4/config'
import useUser from '@/hooks/useUser'
import { useSupportedDataProviders } from '@/components/marketing/hooks/useSupportedDataProviders'
import { useHardwareTier } from '@/hooks/use-hardware-tier'
import { useOrderingCapabilities } from '@/hooks/use-ordering-capabilities'
import { useAccessSubject } from '@/access'
import { useListSquareIntegrationsQuery } from '@/state/neo/neo-square-auth.api'
import morpheusApi from '@/state/morpheus/morpheus.slice'
import wifi from '@/state/wifi/wifi.slice'
import { useOnboardingSetup } from '@/hooks/use-onboarding-setup'
import { parseSchemeId } from '../old-nav-config'

/** Shared NavContext for the old-UI nav (sidebar / top bar / mobile nav). */
export function useNavCtx(): NavContext {
  const { org_id } = useParams<{ org_id: string }>()
  const { pathname } = useLocation()
  const user = useUser()
  const { providers: dataProviders, metadata: dataProviderMetadata } =
    useSupportedDataProviders()
  const s = pathname.split('/').filter(Boolean)
  const serial = s[1] === 'venues' && (s[2]?.length ?? 0) > 0 ? s[2] : undefined
  const { tier: hardwareTier, vendorKey } = useHardwareTier(serial)
  // Gates the Full Bhuna report, whose revenue, item and basket figures all
  // come from Square. Resolved at ORG level because the report is reachable
  // with no venue selected.
  //
  // This has to be duplicated here as well as in `useNewNavCtx`: the old-UI
  // and new-UI navs build their NavContext independently from the SAME item
  // tree, so a gate added to only one context silently hides the item for
  // everyone still on the other nav — which is what happened.
  const { data: squareIntegrations } = useListSquareIntegrationsQuery(
    { organizationId: org_id ?? '' },
    { skip: !org_id }
  )
  const hasSquare = (squareIntegrations?.integrations?.length ?? 0) > 0
  // Ordering (Mouse) gate. Same duplication rule as hasSquare: the ONE hook,
  // called from both ctx builders, never a third computation.
  const { ordering: hasOrdering } = useOrderingCapabilities(org_id)
  // Network type (free/paid) comes from the trinity Wi-Fi config — the same
  // source the Wi-Fi settings page edits. Drives the paid-only / free-only
  // Wi-Fi nav gates (Portal Payments vs Captive Portal Surveys).
  const { data: wifiConfig } = wifi.useGetSettingsQuery(
    { serial: serial ?? '' },
    { skip: !serial }
  )
  // User-scoped, not org-scoped — see the duplication note above re: hasSquare.
  // Must be kept in sync with `useNewNavCtx`, or the "Invitations" item is
  // gated closed for whichever nav doesn't build this field.
  const { data: pendingInvitations } =
    morpheusApi.useGetPendingInvitationsQuery()
  const pendingInvitationsCount = pendingInvitations?.length ?? 0
  // Setup state. THE ONE HOOK, shared with `useNewNavCtx`; see
  // `useOnboardingSetup` for why there is no second copy.
  const onboarding = useOnboardingSetup(org_id)
  // Resolves every gating axis so nav items can carry a `capability`. Must be
  // kept in step with `useNewNavCtx` — see the duplication note above; a gated
  // item with no subject fails closed and disappears from whichever nav omits it.
  const access = useAccessSubject()
  return useMemo(() => {
    return {
      orgId: org_id ?? '',
      hasSquare,
      hasOrdering,
      serial,
      schemeId: parseSchemeId(pathname),
      venue: wifiConfig ?? null,
      userEmail: user?.email,
      dataProviders,
      dataProviderMetadata,
      hardwareTier,
      vendorKey,
      pendingInvitationsCount,
      onboardingNav: onboarding.nav,
      onboardingOutstandingCount: onboarding.outstanding,
      access,
    }
  }, [
    hasSquare,
    hasOrdering,
    org_id,
    pathname,
    serial,
    wifiConfig,
    user?.email,
    dataProviders,
    dataProviderMetadata,
    hardwareTier,
    vendorKey,
    pendingInvitationsCount,
    onboarding.nav,
    onboarding.outstanding,
    access,
  ])
}
